/**
 * MODULE: _shared/frontend-shell/src/hooks/assistant/resolveDefaultAgentId.ts
 * PURPOSE: Default platform agent UUID for unified orchestration streaming (dock + tools).
 * DEPENDENCIES: import.meta.env (Vite)
 * PUBLIC_API: BRAZN_AGENT_* constants, resolveDefaultAgentId, resolveAgentIdForAssistantContext
 * AGENT_SAFETY: SAFE
 * RECENT: 2026-06-05 [2.2.1300] — Topic slug → agent family via inferAssistantTopicFromMessage (NL dispatch parity). [2.2.1294] per-tenant uuid5.
 */

import { inferAssistantTopicFromMessage } from './inferAssistantTopicFromMessage';

/** Legacy global Sales Execution id (pre per-tenant migration) — fallback when tenant is unknown. */
export const BRAZN_AGENT_SALES_EXECUTION_ID = '20ed2ccb-aad9-505d-98d6-a783d42a27e9';

/** Legacy global Pipeline Insights id — fallback when tenant is unknown. */
export const BRAZN_AGENT_PIPELINE_INSIGHTS_ID = '594112a1-c955-5e6c-bd90-f11c88f8c774';

/** Legacy smoke agent (`scripts/seed/smoke_agent_ids.json`) — fallback when tenant is unknown. */
export const BRAZN_AGENT_SMOKE_RESEARCH_ID = '3b63d46b-67ec-4fe9-b48f-c7fee1bddcf6';

/** Acme dev tenant — `uuid5(SEED_NS, "brazn:agent:sales-research:{tenant_id}")`. */
export const BRAZN_AGENT_SALES_RESEARCH_ACME_ID = 'f9f7b4ba-371a-5e9c-a910-edd0e447e061';

/** Test dev tenant (`test@brazn.dev`) — same uuid5 scheme as seed script. */
export const BRAZN_AGENT_SALES_RESEARCH_TEST_ID = '423c9755-dc91-56d3-92ae-2bd76c5b8c0a';

/** Acme — `uuid5(SEED_NS, "brazn:agent:sales-execution:{tenant_id}")`. */
export const BRAZN_AGENT_SALES_EXECUTION_ACME_ID = '6d5e0845-d04f-5c6e-96a1-096bc4fe33e4';

/** Test — per-tenant Sales Execution. */
export const BRAZN_AGENT_SALES_EXECUTION_TEST_ID = '1969ea74-943d-5702-9769-8734374cb216';

/** Acme — `uuid5(SEED_NS, "brazn:agent:pipeline-insights:{tenant_id}")`. */
export const BRAZN_AGENT_PIPELINE_INSIGHTS_ACME_ID = 'f33c9e8c-e4cd-5aa5-8d04-a121c9827a09';

/** Test — per-tenant Pipeline Insights. */
export const BRAZN_AGENT_PIPELINE_INSIGHTS_TEST_ID = '7a20e683-a5d9-5d5a-ab87-905a54a171ef';

const ACME_TENANT_ID = 'cbece855-814a-4630-a6de-ca754f4f360c';
const TEST_TENANT_ID = '11111111-1111-1111-1111-111111111111';

const SALES_RESEARCH_AGENT_BY_TENANT: Record<string, string> = {
  [ACME_TENANT_ID.toLowerCase()]: BRAZN_AGENT_SALES_RESEARCH_ACME_ID,
  [TEST_TENANT_ID.toLowerCase()]: BRAZN_AGENT_SALES_RESEARCH_TEST_ID,
};

const SALES_EXECUTION_AGENT_BY_TENANT: Record<string, string> = {
  [ACME_TENANT_ID.toLowerCase()]: BRAZN_AGENT_SALES_EXECUTION_ACME_ID,
  [TEST_TENANT_ID.toLowerCase()]: BRAZN_AGENT_SALES_EXECUTION_TEST_ID,
};

const PIPELINE_INSIGHTS_AGENT_BY_TENANT: Record<string, string> = {
  [ACME_TENANT_ID.toLowerCase()]: BRAZN_AGENT_PIPELINE_INSIGHTS_ACME_ID,
  [TEST_TENANT_ID.toLowerCase()]: BRAZN_AGENT_PIPELINE_INSIGHTS_TEST_ID,
};

const PLACEHOLDER_FLOW_RE =
  /^(?:00000000-0000-4000-8000-000000000000|your-flow-uuid|paste-uuid)/i;

/** Routes and prefill hints that need Pipeline Insights (deals, KPI, forecast). */
const PIPELINE_CONTEXT_RE =
  /\/crm\/deals\b|\/pipeline\b|\/opportunities\b|crm deals page|deals hub|pipeline health|pipeline by stage|forecast summary|top accounts|top reps/i;

/** Routes and prefill hints for Sales Research (contacts, enrichment, company research). */
const RESEARCH_CONTEXT_RE =
  /\/crm\/contacts\b|\/research\b|contact detail|research account|who is |enrich contact|enrich /i;

/** Workspace / canvas / widget / Sales Hub content — Sales Execution agent family. */
const SALES_EXECUTION_CONTEXT_RE =
  /\/home\b|\/langflow-assistant|\/assistant\b|\/workspace|manage.canvases|manage.widgets|sales hub|list my canvases|create a canvas/i;

/** Topic slugs seeded per agent family (scripts/seed/langflow_topic_slugs.manifest). */
const RESEARCH_TOPIC_SLUGS = new Set([
  'research-account',
  'research-person',
  'build-account-brief',
  'enrich-contact',
  'draft-outreach',
  'stage-lookup',
]);

const SALES_EXECUTION_TOPIC_SLUGS = new Set([
  'schedule-meeting',
  'log-activity',
  'create-task',
  'update-opportunity',
  'draft-follow-up',
  'team-messaging',
  'manage-canvases',
  'manage-widgets',
  'sales-hub-content',
]);

const PIPELINE_TOPIC_SLUGS = new Set([
  'pipeline-by-stage',
  'top-accounts',
  'top-reps',
  'forecast-summary',
  'export-report',
]);

/** Map routed topic slug to the platform agent that owns that Langflow canvas. */
export function resolveAgentIdFromTopicSlug(
  slug: string,
  tenantId?: string | null
): string | undefined {
  const key = (slug || '').trim().toLowerCase();
  if (!key) return undefined;
  if (PIPELINE_TOPIC_SLUGS.has(key)) {
    return resolvePipelineInsightsAgentId(tenantId);
  }
  if (SALES_EXECUTION_TOPIC_SLUGS.has(key)) {
    return resolveSalesExecutionAgentId(tenantId);
  }
  if (RESEARCH_TOPIC_SLUGS.has(key)) {
    return resolveSalesResearchAgentId(tenantId);
  }
  return undefined;
}

function _agentIdFromInferredTopic(
  text: string,
  tenantId?: string | null
): string | undefined {
  const slug = inferAssistantTopicFromMessage(text);
  if (!slug) return undefined;
  return resolveAgentIdFromTopicSlug(slug, tenantId);
}

export type AssistantAgentPageHint = {
  path?: string | null;
  title?: string | null;
  prefillContext?: string | null;
  /** User draft or outgoing message — enables pipeline/sales-exec routing on /langflow-assistant. */
  draftMessage?: string | null;
};

/** Strip optional `tnt_` prefix from JWT tenant claims for agent lookup. */
export function normalizeTenantIdForAgent(tenantId?: string | null): string | undefined {
  const raw = (tenantId ?? '').trim();
  if (!raw) return undefined;
  const prefixed = /^tnt_([0-9a-f-]{36})$/i.exec(raw);
  return (prefixed ? prefixed[1] : raw).toLowerCase();
}

export function resolveSalesResearchAgentId(tenantId?: string | null): string {
  const tid = normalizeTenantIdForAgent(tenantId);
  if (tid && SALES_RESEARCH_AGENT_BY_TENANT[tid]) {
    return SALES_RESEARCH_AGENT_BY_TENANT[tid];
  }
  return BRAZN_AGENT_SMOKE_RESEARCH_ID;
}

export function resolveSalesExecutionAgentId(tenantId?: string | null): string {
  const tid = normalizeTenantIdForAgent(tenantId);
  if (tid && SALES_EXECUTION_AGENT_BY_TENANT[tid]) {
    return SALES_EXECUTION_AGENT_BY_TENANT[tid];
  }
  return BRAZN_AGENT_SALES_EXECUTION_ID;
}

export function resolvePipelineInsightsAgentId(tenantId?: string | null): string {
  const tid = normalizeTenantIdForAgent(tenantId);
  if (tid && PIPELINE_INSIGHTS_AGENT_BY_TENANT[tid]) {
    return PIPELINE_INSIGHTS_AGENT_BY_TENANT[tid];
  }
  return BRAZN_AGENT_PIPELINE_INSIGHTS_ID;
}

/**
 * Agent UUID for GET `/api/v1/assistant/chat/stream` when the shell does not pass an explicit id.
 * Known dev tenants use per-tenant uuid5 agents; else `VITE_DEFAULT_AGENT_ID`; else legacy smoke id.
 */
export function resolveDefaultAgentId(tenantId?: string | null): string | undefined {
  const tid = normalizeTenantIdForAgent(tenantId);
  if (tid && SALES_RESEARCH_AGENT_BY_TENANT[tid]) {
    return SALES_RESEARCH_AGENT_BY_TENANT[tid];
  }
  const fromEnv = (import.meta.env.VITE_DEFAULT_AGENT_ID as string | undefined)?.trim();
  if (fromEnv && !PLACEHOLDER_FLOW_RE.test(fromEnv)) {
    return fromEnv;
  }
  return BRAZN_AGENT_SMOKE_RESEARCH_ID;
}

/**
 * Pick agent family from page path and CRM assistant prefill so pipeline/deals NL
 * does not run on Sales Execution (Sales Hub navigation misroute).
 */
export function resolveAgentIdForAssistantContext(
  hint?: AssistantAgentPageHint | null,
  tenantId?: string | null
): string | undefined {
  const path = (hint?.path ?? '').trim();
  const title = (hint?.title ?? '').trim();
  const prefill = (hint?.prefillContext ?? '').trim();
  const draft = (hint?.draftMessage ?? '').trim();

  const fromDraft = _agentIdFromInferredTopic(draft, tenantId);
  if (fromDraft) return fromDraft;

  const fromPrefill = _agentIdFromInferredTopic(prefill, tenantId);
  if (fromPrefill) return fromPrefill;

  const blob = [path, title, prefill, draft].filter(Boolean).join(' ');

  if (blob && PIPELINE_CONTEXT_RE.test(blob)) {
    return resolvePipelineInsightsAgentId(tenantId);
  }
  if (blob && RESEARCH_CONTEXT_RE.test(blob)) {
    return resolveSalesResearchAgentId(tenantId);
  }
  if (blob && SALES_EXECUTION_CONTEXT_RE.test(blob)) {
    return resolveSalesExecutionAgentId(tenantId);
  }
  return resolveDefaultAgentId(tenantId);
}
